/**
 * resolveSiteElevation — single source of truth for "what elevation
 *   string should the UI surface".
 *
 * Background (ELV pack — row 0.99220, 2026-05-18). Three surfaces in
 * the Site Report + Site Landing Page show elevation:
 *
 *   1. Report → Property → Site Boundary accordion
 *      (`components/siterag/report/ExtendedDataSection.tsx`).
 *   2. Site Overview tab → Site info → Elevation row
 *      (`components/siterag/SiteLandingPage.tsx`).
 *   3. Sim preview chip → Data Pre-loaded from Site
 *      (`components/siterag/SiteLandingPage.tsx`).
 *
 * Pre-ELV, every surface read the primary's `siteElevationRange`
 * triple and rendered "Data unavailable" whenever the saved value was
 * the legacy `{0,0,0}` fingerprint (a pre-EL2 boundary-confirm path
 * clobbered failed elevation lookups to zero). Customer trust impact:
 * sites like Redhead (Louisiana, US-coverage) had a populated
 * `sed.siteProfile.data.elevationAvg` from the synthetic USGS EPQS
 * provider that NOBODY consumed in the report viewer.
 *
 * ELV-1 funnels every surface through this resolver. Priority order:
 *
 *   1. **polygon-sample** — `primary.siteElevationRange` if it's a
 *      real (non-zero, finite) triple. This is the high-fidelity
 *      reading written at boundary-draw time by
 *      `fetchPolygonElevations` (Google Elevation API, up to 10
 *      vertex samples + centroid). Carries `stdev` + `pointsSampled`.
 *
 *   2. **usgs-epqs-centroid** — `sed.siteProfile.data.elevationAvg`
 *      if finite. The `siteProfile` synthetic provider runs against
 *      USGS EPQS for US sites and writes a single-point centroid
 *      sample. We synthesize a degenerate `{min: avg, max: avg, avg,
 *      stdev: null, pointsSampled: 1}` triple so the rest of the UI
 *      can be source-agnostic. Honest labelling lives at the call
 *      site ("centroid, USGS").
 *
 *   3. **null** — no usable elevation. The caller surfaces "Data
 *      unavailable" + an optional "Refresh elevation" CTA.
 *
 * Units: metres ASL throughout. The resolver does NOT round — the
 * call site decides display precision.
 *
 * Pure function, no I/O, no React. Single-export for tree-shaking
 * and trivial unit testing.
 */

import type { SiteEvidenceDocument } from '../types/siteEvidence';

/** Triple-with-stats shape, mirrors `SiteRagReport.siteElevationRange`. */
export interface ElevationTriple {
  min: number;
  max: number;
  avg: number;
  stdev?: number | null;
  pointsSampled?: number;
}

/** Provenance tag for the resolved elevation — surfaced in the UI so
 *  users see *where* the value came from (polygon vs centroid). */
export type ElevationSource =
  | 'polygon-sample'
  | 'usgs-epqs-centroid'
  | 'google-centroid'
  | 'legacy-unknown';

export interface ElevationResolution {
  value: ElevationTriple;
  source: ElevationSource;
}

/** Shape accepted as the "primary" carrier — both `SiteEntity` and
 *  `SiteRagReport` expose a compatible optional `siteElevationRange`. */
export interface ElevationPrimary {
  siteElevationRange?: ElevationTriple;
}

/**
 * Return the best available elevation reading, or `null` when no
 * source has usable data.
 *
 * @param primary  Either a SiteEntity or a SiteRagReport — anything
 *                 carrying an optional `siteElevationRange`.
 * @param sed      The site's evidence document (optional). If
 *                 present, `sed.siteProfile.data.elevationAvg` is the
 *                 fallback after the primary triple.
 */
export function resolveSiteElevation(
  primary: ElevationPrimary | null | undefined,
  sed: SiteEvidenceDocument | null | undefined,
): ElevationResolution | null {
  const stored = primary?.siteElevationRange;
  if (stored) {
    const finite =
      Number.isFinite(stored.min) &&
      Number.isFinite(stored.max) &&
      Number.isFinite(stored.avg);
    const legacyZero = stored.min === 0 && stored.max === 0 && stored.avg === 0;
    if (finite && !legacyZero) {
      return { value: stored, source: 'polygon-sample' };
    }
  }

  // ELV-1 fallback: synthetic siteProfile (USGS EPQS for US sites).
  const sp = sed?.siteProfile?.data;
  const epqsAvg =
    sp && typeof sp.elevationAvg === 'number' && Number.isFinite(sp.elevationAvg)
      ? sp.elevationAvg
      : null;
  if (epqsAvg != null) {
    // If min/max also present + finite (richer EPQS responses), use
    // them so the range is honest rather than collapsed to a point.
    const min =
      sp && typeof sp.elevationMin === 'number' && Number.isFinite(sp.elevationMin)
        ? sp.elevationMin
        : epqsAvg;
    const max =
      sp && typeof sp.elevationMax === 'number' && Number.isFinite(sp.elevationMax)
        ? sp.elevationMax
        : epqsAvg;
    const stdev =
      sp && typeof sp.elevationStdev === 'number' && Number.isFinite(sp.elevationStdev)
        ? sp.elevationStdev
        : null;
    const pointsSampled =
      sp && typeof sp.elevationPointsSampled === 'number' && sp.elevationPointsSampled > 0
        ? sp.elevationPointsSampled
        : 1;
    return {
      value: { min, max, avg: epqsAvg, stdev, pointsSampled },
      source: 'usgs-epqs-centroid',
    };
  }

  return null;
}

/**
 * Pretty-printer for the resolved elevation. Honest about source:
 *   - polygon-sample → "120–140 m (avg 130 m, n=11)"
 *   - usgs-epqs-centroid (synthetic point) → "120 m (centroid, USGS)"
 *   - usgs-epqs-centroid (with real range) → "120–140 m (avg 130 m, USGS)"
 *
 * Always returns metres rounded to the nearest integer (the underlying
 * data is captured to 1dp; reports render integers for readability).
 */
export function formatResolvedElevation(res: ElevationResolution): string {
  const { value, source } = res;
  const min = Math.round(value.min);
  const max = Math.round(value.max);
  const avg = Math.round(value.avg);
  const isCollapsedPoint = min === max && max === avg;
  if (source === 'usgs-epqs-centroid' && isCollapsedPoint) {
    return `${avg} m (centroid, USGS)`;
  }
  if (source === 'usgs-epqs-centroid') {
    return `${min}–${max} m (avg ${avg} m, USGS)`;
  }
  const n = value.pointsSampled;
  const nSuffix = typeof n === 'number' && n > 0 ? `, n=${n}` : '';
  return `${min}–${max} m (avg ${avg} m${nSuffix})`;
}
